import { Component, ElementRef, HostListener, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { cloneDeep, each, has, forEach, findIndex, mergeWith } from 'lodash';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { Enums } from '../../../../../configs/Enums';
import { WORK_SHIFTS_PATHS } from '../../../../work-shifts/work-shifts';
import { EmployeeTimeHeader } from '../employee-statuses/employee-statuses.component';
import { EmployeeStatusChangeModalComponent } from '../employee-statuses/modals/employee-status-change-modal/employee-status-change-modal.component';
import { MonitoringService } from './monitoring.service';
import { EmployeeStatusesService } from '../employee-statuses/employee-statuses.service';
import { TicketViewService } from '../../../../tickets/ticket-view/ticket-view.service';
import { TICKETS_PATHS } from 'src/app/pages/tickets/tickets';
import { AuthGroupTypesEnum } from '../../../../auth/auth-group-types.enum';
import { AuthService } from '../../../../auth/auth.service';

enum MoveDirection {
  Left = 'left',
  Right = 'right',
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-monitoring',
  templateUrl: './monitoring.component.html',
  styleUrls: ['./monitoring.component.scss'],
  standalone: false,
})
export class MonitoringComponent implements OnInit {
  public Enums = Enums;
  public TICKETS_PATHS = TICKETS_PATHS;
  public WORK_SHIFTS_PATHS = WORK_SHIFTS_PATHS;
  public moment = require('moment');
  public sectorId: number | string;
  public isFirstInit: boolean;
  public brigades;
  public hoursOffset;
  public brigadesFiltered;
  public previouslyIncludes = true;
  public filter = {
    calendar: {
      initDate: new Date(),
      selectedDate: null,
      linkDate: null,
    },
    data: {
      employers: null,
      brigades: null,
      tickets: null,
    },
    name: '',
    filterType: 'all',
    currentTime: null,
    isCurrentDay: false,
    onWorkShiftDateChange: this.onWorkShiftDateChange.bind(this),
    onWorkShiftPreviewDateChange: this.onWorkShiftPreviewDateChange.bind(this),
    onWorkShiftNextDateChange: this.onWorkShiftNextDateChange.bind(this),
    onSetCurrentDate: this.onSetCurrentDate.bind(this),
    onSetFilter: this.onSetFilter.bind(this),
    onUpdateWidget: this.onUpdateWidget.bind(this),
  };
  public statuses = [];
  public employers = [];
  public times: EmployeeTimeHeader[] = [];
  public slidePosition: number;
  public countLoadDays: number;
  public countSlideCells: number;
  public countCells: number;
  public countScrolledCells: number;
  public countScrolledCellsForInit: number;
  public loading: boolean;
  public fixedHeader: boolean;
  public dateFullFormat = Enums.dates.fullFormat;
  public dateShortFormat = Enums.dates.shortFormat;
  public ticketsMap = {};
  public isAllBrigadesOpened = false;
  public isAllBrigadeLoading = false;
  public isClickPrevented = null;
  public noAnimation = true;
  public currentDate = null;

  @ViewChild('wrapperHeaderSlide')
  wrapperHeaderSlideElement: ElementRef;
  @ViewChild('monitoringContent')
  monitoringContentElement: ElementRef;
  @ViewChild('wrapperHeaderDaysSlide')
  wrapperHeaderDaysSlideElement: ElementRef;
  @ViewChild('monitoringHeaderWrapper')
  monitoringHeaderWrapperElement: ElementRef;
  @ViewChild('monitoringHeaderFixed')
  monitoringHeaderFixedElement: ElementRef;

  @HostListener('window:scroll.out-zone', []) // notice out-zone
  onWindowScroll() {
    const fixedBlockWidth = this.monitoringHeaderWrapperElement.nativeElement.offsetWidth;
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop;

    if (!fixedBlockWidth) {
      this.fixedHeader = false;
      this.monitoringHeaderFixedElement.nativeElement.style.width = 'auto';
      this.monitoringHeaderFixedElement.nativeElement.classList.remove('wrapper__header--fixed');

      return;
    }

    if (window.pageYOffset >= this.monitoringHeaderWrapperElement.nativeElement.getBoundingClientRect().top + scrollTop - 32) {
      if (!this.fixedHeader) {
        this.fixedHeader = true;
        this.monitoringHeaderFixedElement.nativeElement.style.width = fixedBlockWidth + 'px';
        this.monitoringHeaderFixedElement.nativeElement.classList.add('wrapper__header--fixed');
      }
    } else {
      if (this.fixedHeader) {
        this.fixedHeader = false;
        this.monitoringHeaderFixedElement.nativeElement.style.width = 'auto';
        this.monitoringHeaderFixedElement.nativeElement.classList.remove('wrapper__header--fixed');
      }
    }
  }

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    public modalService: NgbModal,
    private monitoringService: MonitoringService,
    private employeeStatusesService: EmployeeStatusesService,
    private ticketViewService: TicketViewService,
    private authService: AuthService
  ) {}

  ngOnInit() {
    this.initConstants();

    this.route.parent.parent.paramMap.subscribe((paramMap) => {
      this.sectorId = paramMap.get('sector_id') || null;
      this.getMonitoringDataUnion();
    });
  }

  initConstants() {
    this.slidePosition = 0;
    this.countLoadDays = 2;
    this.countSlideCells = 1;
    this.countCells = 24;
    this.hoursOffset = 8;
    this.countScrolledCellsForInit = 7;
    this.fixedHeader = false;
    this.isFirstInit = true;

    if (!this.filter.calendar.initDate) {
      this.filter.calendar.initDate = this.isGRS ? this.moment().utcOffset(0).set({ hour: 0, minute: 0, second: 0, millisecond: 0 }).toDate() : new Date();
    }

    this.currentDate = this.moment(this.filter.calendar.initDate).format('YYYY-MM-DD');
    this.filter.calendar.linkDate = this.moment(this.filter.calendar.initDate).format('YYYY-MM');
  }

  private getEmployee(brigadeId: number): Promise<void> {
    const { intBegin, intEnd } = this.getRange();

    return new Promise((resolve, reject) => {
      this.employeeStatusesService
        .getEmployers({
          int_begin: this.moment(intBegin).format(this.dateShortFormat),
          int_end: this.moment(intEnd).format(this.dateShortFormat),
          sector_id: this.sectorId,
          current_time: this.filter.currentTime,
          brigade_id: brigadeId,
        })
        .subscribe(
          (response) => {
            let sortedEmployers = this.employeeStatusesService.getEmployersSorted(response['data'], intBegin);

            this.setEmployersToBrigade(brigadeId, sortedEmployers).then(
              () => resolve(),
              () => reject()
            );
          },
          () => {
            this.employers = [];
            reject();
          }
        );
    });
  }

  private setEmployersToBrigade(brigadeId: number, employers): Promise<void> {
    return new Promise((resolve, reject) => {
      let brigadeIndex = findIndex(this.brigadesFiltered, {
        id: brigadeId,
      });

      if (brigadeIndex > -1) {
        this.brigadesFiltered[brigadeIndex].employers = employers;
        this.getWorkersStatuses(brigadeId).then(
          () => resolve(),
          () => reject()
        );
      } else {
        resolve();
      }
    });
  }

  getRange() {
    const intBegin = this.moment(this.currentDate);

    const formattedIntBegin = this.isGRS ? intBegin.clone().subtract(1, 'day').toDate() : intBegin.toDate();

    const intEnd = this.isGRS
      ? intBegin.clone().add(this.countLoadDays, 'day').toDate()
      : this.moment(formattedIntBegin)
          .add(this.countLoadDays - 1, 'day')
          .toDate();

    const diff = this.moment(intEnd).diff(intBegin, 'day');
    const range = new Array(diff).fill(0).map((_, index) => this.moment(intBegin).add(index, 'day').format(this.dateShortFormat));

    return {
      intBegin: formattedIntBegin,
      intEnd: intEnd,
      range,
    };
  }

  private getWorkersStatuses(brigadeId): Promise<void> {
    return new Promise((resolve, reject) => {
      let brigadeIndex = findIndex(this.brigadesFiltered, {
        id: brigadeId,
      });

      const { intBegin, intEnd } = this.getRange();

      if (brigadeIndex === -1) {
        return resolve();
      }

      return this.employeeStatusesService
        .getEmployersStatuses({
          int_begin: this.moment(intBegin).format(this.dateShortFormat),
          int_end: this.moment(intEnd).format(this.dateShortFormat),
          sector_id: this.sectorId,
          user_ids: this.brigadesFiltered[brigadeIndex].employers.map((w) => w.u_id),
          current_time: this.filter.currentTime,
          brigade_id: brigadeId,
        })
        .subscribe(
          (response) => {
            this.assignDataToEmployer(brigadeId, response['data'].intervals);
            this.getTicketsUnion(brigadeId).then(
              () => resolve(),
              () => reject()
            );

            this.loading = false;

            setTimeout(() => {
              this.slide();
              this.employeeStatusesService.updateGetDataState(true);
            }, 350);
          },
          () => {
            this.loading = false;
            reject();
          }
        );
    });
  }

  private getMonitoringDataUnion(): void {
    if (this.loading) {
      return;
    }

    const { intBegin, intEnd } = this.getRange();

    this.resetPosition();
    this.loading = true;
    this.isAllBrigadesOpened = false;
    this.monitoringService
      .getMonitoringDataUnion({
        int_begin: this.moment(intBegin).format(this.dateShortFormat),
        int_end: this.moment(intEnd).format(this.dateShortFormat),
        sector_id: this.sectorId,
        search: this.filter.filterType,
        groups: this.isGRS ? [AuthGroupTypesEnum.ServiceEngineerGRS] : [AuthGroupTypesEnum.ServiceEngineer],
        ticket_type: this.isGRS ? '7' : '',
      })
      .subscribe(
        (response) => {
          this.filter.data.brigades = response[0]['data'];
          this.filter.data.employers = response[1]['data'];
          this.filter.data.tickets = response[2]['data'];
          this.filter.currentTime = response[3]['data'].current_time;
          this.preventClicks(null);
          // this.brigades = this.makeBrigades(this.filter.data.employers.brigades);
          const clonedBrigades = cloneDeep(this.filter.data.employers.brigades.all);
          this.brigadesFiltered = this.brigadesFiltered
            ? mergeWith(this.brigadesFiltered, clonedBrigades, (objValue, srcValue) => ({ ...objValue, ...(srcValue || {}) }))
            : clonedBrigades;

          this.checkCurrentDate();
          this.makeTimes();
          this.loading = false;
          this.scrollToPosition(this.countScrolledCellsForInit);

          return this.getEmployers();
        },
        () => {
          this.brigades = null;
          this.brigadesFiltered = null;
          this.filter.data.brigades = null;
          this.filter.data.employers = null;
          this.filter.data.tickets = null;
          this.loading = false;
        }
      );
  }

  private getEmployers() {
    const brigades = this.brigadesFiltered.filter((brigade) => brigade.isOpened && Array.isArray(brigade.employers));

    if (!brigades.length) {
      return Promise.resolve();
    }

    const promises = brigades.map((brigade) => {
      brigade.brigadeDataLoading = true;
      return this.getEmployee(brigade.id);
    });

    return Promise.all(promises).then(() => {
      brigades.forEach((brigade) => (brigade.brigadeDataLoading = false));
    });
  }

  private makeTimes(): void {
    this.times = [];

    const { intBegin } = this.getRange();

    const actualCountDays = this.isGRS ? this.countLoadDays + 1 : this.countLoadDays;

    for (let i = 0; i < actualCountDays; ++i) {
      let date = this.moment(intBegin).add(i, 'day').format(this.dateShortFormat);
      let t = [];

      for (let k = 0; k < 24; ++k) {
        t.push(k < 10 ? '0' + k + '.00' : k + '.00');
      }

      this.times.push({ date: date, times: t });
    }
  }

  private checkCurrentDate(): void {
    let initDateMillis = this.moment(this.filter.calendar.initDate).format(this.dateShortFormat);
    let currentDateMillis = this.moment(this.filter.currentTime).format(this.dateShortFormat);

    this.filter.isCurrentDay = initDateMillis === currentDateMillis;
  }

  private assignDataToEmployer(brigadeId, statuses) {
    let brigadeIndex = findIndex(this.brigadesFiltered, { id: brigadeId });

    if (brigadeIndex > -1) {
      forEach(this.brigadesFiltered[brigadeIndex].employers, (employee) => {
        let i = findIndex(statuses, { user_id: employee.u_id });

        if (i > -1) {
          employee.statuses = statuses[i].intervals;
          employee.worklog = statuses[i].worklog;
        }

        this.makeCurrentDay(employee);
        this.groupByDay(employee);
      });
    }
  }

  private getTicketsUnion(brigadeId): Promise<void> {
    return new Promise((resolve, reject) => {
      let brigadeIndex = findIndex(this.brigadesFiltered, {
        id: brigadeId,
      });

      const { intBegin, intEnd } = this.getRange();

      if (brigadeIndex > -1) {
        this.monitoringService
          .getTicketsUnion({
            sector_id: this.sectorId,
            int_begin: this.moment(intBegin).startOf('day').format(this.dateFullFormat),
            int_end: this.moment(intEnd).format(this.dateFullFormat),
            brigade_id: brigadeId,
            current_time: this.filter.currentTime,
          })
          .subscribe(
            (response) => {
              this.brigadesFiltered[brigadeIndex].statusesData = response[0]['data'];
              this.brigadesFiltered[brigadeIndex].tickets = response[1]['data'];
              this.assignDataToTickets(this.brigadesFiltered[brigadeIndex].tickets).then(
                () => resolve(),
                () => reject()
              );
              this.getTicketsLineInfo(brigadeIndex);
            },
            () => {
              this.brigadesFiltered[brigadeIndex].statusesData = null;
              this.brigadesFiltered[brigadeIndex].invervalsData = null;
              reject();
            }
          );
      } else {
        resolve();
      }
    });
  }

  private getTicketsLineInfo(brigadeIndex) {
    const tickets = this.getTicketsForLineInfo(brigadeIndex);

    if (!tickets || !tickets.length) {
      return;
    }

    this.ticketViewService.getHdAbonentStatus({ abon_info: tickets }).subscribe(
      (response) => {
        this.setLineInfoToTickets(response.data);
      },
      (error) => {
        console.log(error);
      }
    );
  }

  private getTicketsForLineInfo(brigadeIndex) {
    const tickets = [];
    const ticketTypes = [1, 2, 3];

    each(this.brigadesFiltered[brigadeIndex].tickets, (ticket) => {
      if (ticket.ticket_type && ticketTypes.indexOf(ticket.ticket_type.id) > -1) {
        tickets.push({
          ticket_id: ticket.id,
          login: ticket.ticket_login || '',
        });
        this.ticketsMap[ticket.id] = ticket;
      }
    });

    return tickets;
  }

  private setLineInfoToTickets(tickets) {
    each(tickets, (ticket) => {
      this.ticketsMap[ticket.ticket_id].hd_line_status_info = ticket.code;
    });
  }

  private makeCurrentDay(employee): void {
    employee.currentWorkDay = {
      isWorked: false,
      isWorkedOtherSector: false,
      cellType: null,
      sector: null,
      int_begin: '',
      int_end: '',
    };
    if (has(employee, 'working_shift') && employee.working_shift.length) {
      let currentDate = this.moment(this.filter.calendar.initDate).format(this.dateShortFormat);
      let indexWorkDay = findIndex(employee.working_shift, {
        workday: currentDate,
      });

      if (indexWorkDay === -1) {
        return;
      }

      let schedule = employee.working_shift[indexWorkDay].schedule;

      employee.currentWorkDay.sector = (schedule && schedule.sector) || null;
      employee.currentWorkDay.cellType = (schedule && schedule.cell_type) || null;
      employee.currentWorkDay.isWorked = !!((schedule && schedule.cell_type) || schedule.sector);

      // Для формирования ссылки на страницу графика нужен sector_id, в случае командировки берем из
      // гостевого участка
      if (has(schedule, 'sector_id') && schedule.sector_id) {
        employee.sector_id = schedule.sector_id;

        if (schedule.sector_id !== Number(this.sectorId)) {
          employee.currentWorkDay.isWorkedOtherSector = true;
        }
      }

      if ((has(schedule, 'sector_id') && !schedule.sector_id) || schedule.sector_id === Number(this.sectorId)) {
        if (schedule.int_begin && schedule.int_end) {
          employee.currentWorkDay.int_begin = (schedule && schedule.int_begin) || null;
          employee.currentWorkDay.int_end = (schedule && schedule.int_end) || null;
        }
      }
    }
  }

  private groupByDay(employee): void {
    let groupStatusesDate = {};

    employee.groupByDays = { days: [], worked: {} };

    // группировка статусов по датам, так как приходят не сгруппированные в одном массиве
    forEach(employee.statuses, (s) => {
      let intDate = s.int_begin.slice(0, 10);

      if (!employee.groupByDays.worked[s.int_type] && s.int_type) {
        employee.groupByDays.worked[s.int_type] = [];
      }
      if (!groupStatusesDate[intDate]) {
        groupStatusesDate[intDate] = [];
      }
      if (intDate === this.currentDate && s.int_type) {
        employee.groupByDays.worked[s.int_type].push(s);
      }

      // Этим условием отсекаем статусы с int_type = 0, а также исключительную ситуацию когда int_begin = int_end.
      if (s.int_type && s.int_begin !== s.int_end) {
        groupStatusesDate[intDate].push(s);
      }
    });

    const { intBegin } = this.getRange();

    // собираем данные по дням(рабочий интервал, ячейки, статусы)
    for (let i = 0; i < this.countLoadDays; ++i) {
      let date = this.moment(intBegin).add(i, 'day').format(this.dateShortFormat);
      let indexWorkDay = findIndex(employee.working_shift, {
          workday: date,
        }),
        day = {
          workday: date,
          times: Array.apply(0, new Array(24)).map((k, index) => index),
          workInterval: null,
          workStatuses: groupStatusesDate[date] || null,
        };

      if (indexWorkDay > -1) {
        let schedule = employee.working_shift[indexWorkDay]['schedule'] || null;

        if ((has(schedule, 'sector_id') && !schedule.sector_id) || schedule.sector_id === Number(this.sectorId)) {
          if (schedule.int_begin && schedule.int_end) {
            day.workInterval = {
              int_begin: schedule.int_begin || null,
              int_end: schedule.int_end || null,
            };
          } else {
            day.workInterval = null;
          }
        }
      }

      employee.groupByDays.days.push(day);
    }
  }

  private assignDataToTickets(tickets): Promise<void> {
    return new Promise((resolve) => {
      tickets.forEach((ticket) => {
        this.groupByTicketsDay(ticket);
      });

      resolve();
    });
  }

  private groupByTicketsDay(ticket): void {
    let statuses = this.isGRS ? [1, 2] : [3, 4, 5, 7, 9];
    let groupStatusesDate = {};

    ticket.groupByDays = { days: [], worked: {} };
    const { intBegin } = this.getRange();

    forEach(ticket.intervals, (s) => {
      let intDate = s.int_begin.slice(0, 10);

      if (!ticket.groupByDays.worked[s.int_type] && s.int_type) {
        ticket.groupByDays.worked[s.int_type] = [];
      }
      if (!groupStatusesDate[intDate]) {
        groupStatusesDate[intDate] = [];
      }

      if (statuses.indexOf(s.int_type) > -1) {
        groupStatusesDate[intDate].push(s);
        ticket.groupByDays.worked[s.int_type].push(s);
      }
    });

    // собираем данные по дням(рабочий интервал, ячейки, статусы)
    const actualCountDays = this.isGRS ? this.countLoadDays + 1 : this.countLoadDays;
    for (let i = 0; i < actualCountDays; ++i) {
      let date = this.moment(intBegin).add(i, 'day').format(this.dateShortFormat);
      let day = {
        workday: date,
        times: Array.apply(0, new Array(24)).map((k, index) => index),
        workInterval: null,
        workStatuses: groupStatusesDate[date] || null,
      };

      if (ticket.hd_schedule_intbegin && ticket.hd_schedule_intend) {
        day.workInterval = {
          int_begin: ticket.hd_schedule_intbegin || null,
          int_end: ticket.hd_schedule_intend || null,
        };
      }

      ticket.groupByDays.days.push(day);
    }
  }

  public scrollToPosition(countCells): void {
    this.noAnimation = true;
    setTimeout(() => {
      const initialSlidePosition = this.slidePosition;
      const wrapperWidth = this.getTimeWrapperWidth();
      const cellWidth = wrapperWidth / (this.countLoadDays * this.countCells);
      const offset = cellWidth * countCells;
      this.slidePosition -= offset;

      this.slide();
    }, 350);
  }

  public onWorkShiftDateChange(event): void {
    if (event && event.value) {
      this.filter.calendar.selectedDate = event.value;
      this.filter.calendar.linkDate = this.moment(event.value).format(Enums.dates.monthFormat);
      this.currentDate = this.moment(event.value).format(Enums.dates.dateFormat);
      this.getMonitoringDataUnion();
    }
  }

  public onWorkShiftPreviewDateChange(): void {
    const date = this.moment(this.filter.calendar.selectedDate).startOf('day').add(-1, 'day').format(Enums.dates.dateTimeFormat);

    this.filter.calendar.initDate = new Date(date);
    this.currentDate = this.moment(this.filter.calendar.initDate).format(Enums.dates.dateFormat);
    this.filter.calendar.linkDate = this.moment(this.filter.calendar.initDate).format(Enums.dates.monthFormat);

    this.getMonitoringDataUnion();
  }

  public onWorkShiftNextDateChange(): void {
    const date = this.moment(this.filter.calendar.selectedDate).startOf('day').add(1, 'day').format(Enums.dates.dateTimeFormat);

    this.filter.calendar.initDate = new Date(date);
    this.currentDate = this.moment(this.filter.calendar.initDate).format(Enums.dates.dateFormat);
    this.filter.calendar.linkDate = this.moment(this.filter.calendar.initDate).format(Enums.dates.monthFormat);

    this.getMonitoringDataUnion();
  }

  public onSetCurrentDate(): void {
    this.filter.calendar.initDate = new Date();
    this.currentDate = this.moment(this.filter.calendar.initDate).format(Enums.dates.dateFormat);
    this.filter.calendar.linkDate = this.moment(this.filter.calendar.initDate).format(Enums.dates.monthFormat);
    this.getMonitoringDataUnion();
  }

  public onOpenChangeStatusDialog(employee, brigade): void {
    const employeeChangeStatusDialog = this.modalService.open(EmployeeStatusChangeModalComponent, {
      ariaLabelledBy: 'modal-basic-title',
      windowClass: 'modal-wrapper',
      backdrop: 'static',
    });

    employeeChangeStatusDialog.componentInstance.employee = employee;
    employeeChangeStatusDialog.componentInstance.sectorId = this.sectorId;
  }

  public get isGRS() {
    return this.authService.isGRSUser();
  }

  public trackByFn(index, item) {
    return item.workday;
  }

  public trackByIndex(index) {
    return index;
  }

  public resetPosition(): void {
    this.noAnimation = true;
    this.slidePosition = 0;
    this.countLoadDays = 2;
    this.countScrolledCells = 0;
    this.hoursOffset = 8;
    this.countSlideCells = 1;
    this.countCells = 24;
    this.fixedHeader = false;
  }

  public getTimeWrapperWidth(): number {
    return this.monitoringContentElement.nativeElement.querySelector('.content-time-block__wrapper').offsetWidth;
  }

  public getCountTimeWidth(): number {
    return (this.getTimeWrapperWidth() / (this.countLoadDays * this.countCells)) * this.countCells;
  }

  public getCountSlide(): number {
    return (this.getTimeWrapperWidth() / (this.countLoadDays * this.countCells)) * this.countSlideCells;
  }

  public onPrevSlide(): void {
    if (this.slidePosition >= 0) {
      this.preventClicks();
      this.onWorkShiftPreviewDateChange();
      return;
    }

    this.slidePosition += this.isGRS && Math.abs(this.slidePosition) === this.getCountSlide() * 24 ? this.getCountSlide() * 24 : this.getCountSlide();

    this.noAnimation = false;
    this.preventClicks();
    setTimeout(() => {
      this.slide();
      this.updateCurrentDate('left');
      this.preventClicks(null);
    });
  }

  preventClicks(state = true) {
    setTimeout(() => {
      this.isClickPrevented = state;
    });
  }

  public onNextSlide(): void {
    this.slidePosition -= this.getCountSlide();

    if (this.getTimeWrapperWidth() + this.slidePosition < this.getCountTimeWidth()) {
      this.preventClicks();
      return this.onWorkShiftNextDateChange();
    }

    if (this.slidePosition - -(this.getTimeWrapperWidth() / 48) === -this.getCountTimeWidth()) {
      return;
    }

    this.noAnimation = false;
    setTimeout(() => {
      this.slide();
      this.updateCurrentDate('right');
    });
  }

  getTimelineWidth() {
    return (document.querySelector('.timeline') as HTMLElement).offsetWidth;
  }

  getCursor(direction: MoveDirection) {
    const pxPerDay = this.getCountSlide() * 24;
    if (!pxPerDay) {
      console.warn('[getCursor] pxPerDay is zero, cannot calculate cursor.');
      return null;
    }

    // Всегда вычисляем позицию контента, соответствующую левому краю viewport
    const positionAtLeftEdge = Math.abs(this.slidePosition);

    const dayIndex: number = Math.floor(positionAtLeftEdge / pxPerDay);

    if (!this.times || dayIndex < 0 || dayIndex >= this.times.length) {
      console.error(
        `[getCursor] Calculated dayIndex ${dayIndex} is out of bounds for times array (length: ${
          this.times ? this.times.length : 'undefined'
        }). positionAtLeftEdge=${positionAtLeftEdge}, pxPerDay=${pxPerDay}`
      );
      return null;
    }

    // Явно преобразуем к строке, чтобы избежать ошибки типа
    const slideDateStr: string = String(this.times[dayIndex].date);
    const slideDateMoment = this.moment(slideDateStr, this.dateShortFormat);
    if (!slideDateMoment.isValid()) {
      console.error(`[getCursor] Failed to parse slideDateStr: ${slideDateStr}`);
      return null;
    }
    const slideDate: Date = slideDateMoment.toDate();

    // Вычисляем час на основе остатка позиции внутри дня
    const positionWithinDay = positionAtLeftEdge % pxPerDay;
    const slideHour = Math.floor(positionWithinDay / this.getCountSlide());

    const cursorDateTime = new Date(slideDate.setHours(slideHour));

    return {
      cursorDateTime,
      dayIndex,
    };
  }

  updateCurrentDate(direction) {
    const { cursorDateTime, dayIndex } = this.getCursor(direction);
    const rightBorder = this.moment(this.currentDate).endOf('day').add(this.hoursOffset, 'hours');
    const leftBorder = this.moment(this.currentDate).startOf('day').subtract(this.hoursOffset, 'hours');

    const includes = this.moment(cursorDateTime).isSameOrBefore(rightBorder) && this.moment(cursorDateTime).isSameOrAfter(leftBorder);

    if (this.previouslyIncludes !== includes) {
      this.currentDate = this.times[dayIndex].date;

      this.brigadesFiltered
        .filter((b) => b.isOpened)
        .forEach(
          (b) =>
            Array.isArray(b.employers) &&
            b.employers.forEach((employee) => {
              this.makeCurrentDay(employee);
              this.groupByDay(employee);
            })
        );
    }

    this.previouslyIncludes = includes;
  }

  public onSetFilter(type): void {
    this.filter.filterType = type;
    this.brigadesFiltered = cloneDeep(this.filter.data.employers.brigades[type]);
  }

  public slide(): void {
    let elements = this.monitoringContentElement.nativeElement.querySelectorAll('.content-time-block__wrapper');

    this.wrapperHeaderSlideElement.nativeElement.style.left = this.slidePosition + 'px';
    this.wrapperHeaderDaysSlideElement.nativeElement.style.left = this.slidePosition + 'px';
    [].forEach.call(elements, (elem) => {
      elem.style.left = this.slidePosition + 'px';
    });
  }

  public onChangeBrigadeStateOpened(brigade, isOpen): void {
    brigade.isOpened = isOpen || !brigade.isOpened;
    brigade.isBrigadeTicketsOpened = true;

    if (brigade.isOpened) {
      brigade.brigadeDataLoading = true;
      this.getEmployee(brigade.id).then(
        () => (brigade.brigadeDataLoading = false),
        () => (brigade.brigadeDataLoading = false)
      );
    }
  }

  public onToggleAllBrigades(): void {
    this.isAllBrigadesOpened = !this.isAllBrigadesOpened;

    if (this.isAllBrigadesOpened) {
      const brigades = this.brigadesFiltered.filter((brigade) => !brigade.isOpened);

      if (this.brigadesFiltered.length) {
        this.isAllBrigadeLoading = true;
        Promise.all(
          brigades.map((brigade) => {
            brigade.isOpened = true;
            brigade.isBrigadeTicketsOpened = true;
            brigade.brigadeDataLoading = true;

            return this.getEmployee(brigade.id);
          })
        ).then(
          () => {
            each(this.brigadesFiltered, (brigade) => {
              brigade.brigadeDataLoading = false;
            });

            this.isAllBrigadeLoading = true;
          },
          (error) => {
            console.log(error);
          }
        );
      }
    } else {
      each(this.brigadesFiltered, (brigade) => {
        brigade.isOpened = false;
        brigade.brigadeDataLoading = false;
      });
    }
  }

  public onChangeBrigadeTicketsStateOpened(brigade): void {
    brigade.isBrigadeTicketsOpened = !brigade.isBrigadeTicketsOpened;
  }

  public onUpdateWidget(): void {
    this.getMonitoringDataUnion();
  }
}
